import type { CallWidget, TimeSpan } from "./types";

/** The Calls page's write-up card: the widget the backend keeps for each call. */
export const WRITEUP_WIDGET = "call_writeup";

/** What the page shows of a call's write-up, read off its card. */
export type Writeup = {
  /** False while the card is missing, pending or failed: the page says "Writing…". */
  ready: boolean;
  brief: string | null;
  summary: string | null;
  actionPoints: string[];
  /** Chapters, in seconds from the start of the recording. */
  topics: TimeSpan[];
};

const NOT_READY: Writeup = {
  ready: false,
  brief: null,
  summary: null,
  actionPoints: [],
  topics: [],
};

function section(card: CallWidget, name: string): string {
  return card.sub_widgets.find((sub) => sub.display.section === name)?.body ?? "";
}

/**
 * The write-up from a page's cards. A `ready` card has Brief, Summary and Next
 * steps as sub-widgets (the steps are markdown bullets) and the chapters on
 * `display.topics`. Anything else is still being written. (A call with nothing
 * said has an `empty` card, which the server does not serve; it answers
 * `writeup_ready` for it, which `withWriteup` honours.)
 */
export function writeupFromCards(widgets: CallWidget[] | undefined): Writeup {
  const card = widgets?.find((widget) => widget.widget_key === WRITEUP_WIDGET);
  if (card?.status !== "ready") return NOT_READY;
  return {
    ready: true,
    brief: section(card, "brief"),
    summary: section(card, "summary"),
    actionPoints: section(card, "next_steps")
      .split("\n")
      .map((line) => line.replace(/^- /, "").trim())
      .filter(Boolean),
    topics: (card.display.topics ?? []).map((topic) => ({
      label: topic.label,
      start: topic.start_seconds,
      end: topic.end_seconds,
    })),
  };
}

/**
 * A page response with its write-up fields read off the write-up card, which is
 * where they live; the page's components read `brief`, `summary`,
 * `action_points`, `topics` and `writeup_ready` as before. While the card is
 * not ready the server's own fields stand: for a call with no transcript they
 * hold "This call has no transcript.", which a card-only read would blank.
 */
export function withWriteup<
  T extends {
    widgets: CallWidget[];
    writeup_ready: boolean;
    brief?: string | null;
    summary?: string | null;
    action_points?: string[];
    topics?: TimeSpan[];
  },
>(
  data: T,
): T & {
  brief: string | null;
  summary: string | null;
  action_points: string[];
  topics: TimeSpan[];
} {
  const writeup = writeupFromCards(data.widgets);
  if (!writeup.ready) {
    return {
      ...data,
      brief: data.brief ?? null,
      summary: data.summary ?? null,
      action_points: data.action_points ?? [],
      topics: data.topics ?? [],
    };
  }
  return {
    ...data,
    brief: writeup.brief,
    summary: writeup.summary,
    action_points: writeup.actionPoints,
    topics: writeup.topics,
    writeup_ready: true,
  };
}
